import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useChatbot } from '../contexts/ChatbotContext';
import { useConversationStats } from '../hooks/useConversationStats';
import { supabase } from '../lib/supabase';

interface ChartData {
  date: string;
  conversations: number;
  leads: number;
  duration: number;
}

export default function Statistics() {
  const { stats, loading: statsLoading } = useConversationStats();
  const { chatbotPublicId, isLoading: isChatbotLoading } = useChatbot();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d');
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Don't fetch data if chatbot context is still loading
    if (isChatbotLoading || !chatbotPublicId) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        const startDate = subDays(new Date(), days);

        // Récupérer les conversations using chatbotPublicId
        const { data: conversations, error: conversationsError } = await supabase
          .from('conversations')
          .select('id, created_at, duration_seconds')
          .eq('chatbot_public_id', chatbotPublicId)
          .gte('created_at', startDate.toISOString())
          .order('created_at');

        if (conversationsError) throw conversationsError;

        // Récupérer les leads for these conversations
        const { data: leads, error: leadsError } = await supabase
          .from('conversation_contact_info')
          .select('id, created_at, conversation_id')
          .eq('chatbot_public_id', chatbotPublicId)

        if (leadsError) throw leadsError;

        // Organiser les données par jour
        const data: ChartData[] = [];
        for (let i = 0; i < days; i++) {
          const date = subDays(new Date(), days - 1 - i);
          const dateStr = format(date, 'dd/MM', { locale: fr });
          const startOfDay = new Date(date.setHours(0, 0, 0, 0));
          const endOfDay = new Date(date.setHours(23, 59, 59, 999));

          const dayConversations = conversations?.filter(c => 
            new Date(c.created_at) >= startOfDay && 
            new Date(c.created_at) <= endOfDay
          ) || [];

          const dayLeads = leads?.filter(l => 
            new Date(l.created_at) >= startOfDay && 
            new Date(l.created_at) <= endOfDay
          ) || [];

          data.push({
            date: dateStr,
            conversations: dayConversations.length,
            leads: dayLeads.length,
            duration: dayConversations.length > 0 
              ? dayConversations.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) / dayConversations.length
              : 0
          });
        }

        setChartData(data);
      } catch (err) {
        console.error('Error fetching chart data:', err);
        setError('Failed to load chart data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, chatbotPublicId, isChatbotLoading]);

  if (loading || statsLoading || isChatbotLoading) {
    return (
      <div className="p-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex justify-center items-center h-64 text-red-400">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Statistics</h1>
        <p className="text-gray-400">Analyze your chatbot's performance</p>
      </div>

      {/* Sélecteur de période */}
      <div className="mb-8 flex gap-4">
        <button
          onClick={() => setTimeRange('7d')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            timeRange === '7d'
              ? 'bg-indigo-500/20 text-indigo-400'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          7 days
        </button>
        <button
          onClick={() => setTimeRange('30d')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            timeRange === '30d'
              ? 'bg-indigo-500/20 text-indigo-400'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          30 days
        </button>
        <button
          onClick={() => setTimeRange('90d')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            timeRange === '90d'
              ? 'bg-indigo-500/20 text-indigo-400'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          90 days
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Graphique des conversations */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-6">Conversations</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#F3F4F6'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="conversations"
                  stroke="#6366F1"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Graphique des leads */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-6">Collected leads</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#F3F4F6'
                  }}
                />
                <Bar dataKey="leads" fill="#6366F1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Graphique du temps moyen */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-6">Average time per conversation</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#F3F4F6'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="duration"
                  stroke="#EC4899"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Statistiques globales */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-6">Global statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Total conversations</p>
              <p className="text-2xl font-bold text-white">{stats.totalConversations}</p>
              <p className={`text-sm ${stats.conversationTrend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.conversationTrend > 0 ? '+' : ''}{stats.conversationTrend}%
              </p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Collected leads</p>
              <p className="text-2xl font-bold text-white">{stats.leadsCollected}</p>
              <p className={`text-sm ${stats.leadsCollectedTrend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.leadsCollectedTrend > 0 ? '+' : ''}{stats.leadsCollectedTrend}%
              </p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Average time</p>
              <p className="text-2xl font-bold text-white">{stats.averageDuration}</p>
              <p className={`text-sm ${stats.durationTrend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.durationTrend > 0 ? '+' : ''}{stats.durationTrend}%
              </p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Conversion rate</p>
              <p className="text-2xl font-bold text-white">{stats.conversionRate}%</p>
              <p className={`text-sm ${stats.conversionTrend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.conversionTrend > 0 ? '+' : ''}{stats.conversionTrend}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}